import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { greeting } from "portfolio";
import SocialMedia from "components/socialMedia/SocialMedia";
import TypedText from "components/typedText/TypedText";
import { useScrollReveal } from "hooks/useScrollReveal";
import { useVisitorInfo } from "hooks/useVisitorInfo";
import { useGreetingAnimations } from "hooks/useGreetingAnimations";
import { Button } from "components/ui/Button";
import { StatCard } from "components/greeting/StatCard";
import { LiveStatusBadge } from "components/greeting/Badges";
import { SystemStatus } from "components/greeting/SystemStatus";
import { ProfileCard } from "components/greeting/ProfileCard";
import type { Theme } from "types";
import RevealQuest from "components/gamification/RevealQuest";

interface GreetingProps {
  theme: Theme;
}

const TYPED_TEXTS = [
  "Backend systems for data-heavy products",
  "Cloud-native architecture for scale",
  "AI-enhanced workflows and platform tooling",
  "Engineering leadership with product clarity",
  "Microservices, APIs, and resilient systems",
];

const STATS = [
  { value: "7+", label: "Years Exp." },
  { value: "20+", label: "Projects" },
  { value: "90%", label: "Satisfied" },
];

const HERO_LINES = ["Hello,", "welcome to my website"];

function getFlagEmoji(countryCode: string): string {
  if (!countryCode) return "";
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + (char.codePointAt(0) ?? 0));
  return String.fromCodePoint(...codePoints);
}

export default function Greeting({ theme }: Readonly<GreetingProps>) {
  const navigate = useNavigate();
  const nameRef = useRef<HTMLHeadingElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const statsReveal = useScrollReveal({ threshold: 0.3 });
  const buttonsReveal = useScrollReveal({ threshold: 0.2 });

  // RevealQuest helpers: hover the title for 2s to trigger an event
  const hoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  function handleTitleEnter() {
    hoverTimerRef.current = globalThis.setTimeout(() => {
      globalThis.dispatchEvent(new CustomEvent("revealQuest:hoverTitle"));
    }, 2000);
  }
  function handleTitleLeave() {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }

  const visitor = useVisitorInfo();
  useGreetingAnimations(titleRef, nameRef);

  const profileImageSrc = greeting.profile_image_path
    ? require(`assests/images/${greeting.profile_image_path}`)
    : null;

  const nameText = "I'm Rexford Koomson";
  const nameGlyphs = Array.from(nameText);

  return (
    <section className="relative flex items-center min-h-[92svh] px-4 pt-24 pb-12 sm:px-8 sm:pt-28 sm:pb-20 lg:px-12 lg:pt-32 lg:pb-24">
      <div className="relative z-10 w-full max-w-6xl mx-auto">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[55fr_45fr] lg:gap-20 lg:items-center">
          {/* ── Left Column ── */}
          <div className="flex flex-col gap-6 sm:gap-8">
            <LiveStatusBadge />

            {/* Heading block */}
            <div className="relative flex flex-col gap-3 pb-6 border-b hero-heading-shell border-border">
              <span className="hero-heading-glow" aria-hidden="true" />
              <p className="font-mono text-xs tracking-[0.12em] uppercase text-text-muted terminal-prefix">
                Fullstack Engineer — AI-Native Applications
              </p>

              <h1
                ref={titleRef}
                onMouseEnter={handleTitleEnter}
                onMouseLeave={handleTitleLeave}
                className="hero-kinetic-title text-[clamp(2.5rem,11vw,6.5rem)] font-display font-bold leading-[0.94] tracking-[-0.04em] text-text-primary flex flex-col"
              >
                {HERO_LINES.map((line) => (
                  <span key={line} className="title-letter hero-line-wrap">
                    {line.split(" ").map((word, index) => (
                      <span key={`${line}-${word}`} className="hero-word">
                        {word}
                        {index < line.split(" ").length - 1 ? "\u00A0" : ""}
                      </span>
                    ))}
                    <span className="hero-beat" aria-hidden="true" />
                  </span>
                ))}
              </h1>
              <h2
                ref={nameRef}
                className="hero-signature text-[clamp(1.6rem,6.5vw,3.5rem)] font-display font-light italic tracking-[-0.01em] leading-tight text-text-secondary"
              >
                {nameGlyphs.map((glyph, index) => (
                  <span
                    key={`${glyph}-${index}`}
                    className="inline-block letter hero-name-letter"
                  >
                    {glyph === " " ? "\u00A0" : glyph}
                  </span>
                ))}
              </h2>
              <p className="max-w-[520px] text-[clamp(0.95rem,1.8vw,1.05rem)] leading-relaxed text-text-muted mt-2">
                {greeting.subTitle}
              </p>
            </div>

            {/* Typed text */}
            <div className="max-w-[520px] min-h-[56px] py-3 border-y border-border font-mono text-sm text-text-muted">
              <span className="mr-2 text-accent">$</span>
              <TypedText
                texts={TYPED_TEXTS}
                typingSpeed={80}
                deletingSpeed={40}
                pauseDuration={2500}
              />
              <span className="animate-blink text-accent ml-0.5">|</span>
            </div>

            {/* Visitor */}
            {visitor.country && (
              <p className="font-mono text-xs text-text-muted">
                <span className="mr-2 text-accent">→</span>
                Visitor from {getFlagEmoji(visitor.countryCode)}{" "}
                {visitor.city && `${visitor.city}, `}
                {visitor.country}
              </p>
            )}

            {/* Social links. A plain presentational wrapper (not a button) so
                the real <a> tags stay valid and keyboard-accessible; the quest
                event is fired from the links themselves via onLinkClick. */}
            <div className="transition-opacity opacity-60 hover:opacity-100 focus-within:opacity-100">
              <SocialMedia
                onLinkClick={() =>
                  globalThis.dispatchEvent(
                    new CustomEvent("revealQuest:clickSocial"),
                  )
                }
              />
            </div>

            {/* CTA Buttons */}
            <div
              ref={buttonsReveal.ref}
              className={`flex flex-col sm:flex-row sm:flex-wrap gap-3 transition-all duration-600 ${buttonsReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
            >
              <Button
                variant="primary"
                size="lg"
                ripple
                onClick={() => navigate("/contact")}
              >
                Contact Me
              </Button>

              {greeting.resumeLink && (
                <Button
                  variant="outline"
                  size="lg"
                  href={greeting.resumeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Resume
                </Button>
              )}
            </div>

            {/* Mobile: toggleable skill matcher (visible on small screens) */}
            <div className="lg:hidden">
              <MobileSkillToggle />
            </div>

            {/* Stats */}
            <div
              ref={statsReveal.ref}
              className={`grid grid-cols-3 pt-8 border-t border-border transition-all duration-600 ${statsReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
            >
              {STATS.map((stat) => (
                <StatCard
                  key={stat.label}
                  value={stat.value}
                  label={stat.label}
                />
              ))}
            </div>
          </div>

          {/* ── Right Column: System Status ── */}
          <div className="hidden lg:flex lg:flex-col lg:gap-4">
            <ProfileCard
              theme={theme}
              nameText={greeting.title}
              nickname={greeting.nickname}
              fullName={greeting.full_name}
              profileImageSrc={profileImageSrc}
            />
            <SystemStatus />
            <div>
              <RevealQuest />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MobileSkillToggle(): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div className="p-3 border rounded-xl border-border bg-bg-elevated/50">
      <button
        onClick={() => setOpen((s) => !s)}
        className="w-full px-4 py-3 mb-2 text-sm font-medium text-white rounded-lg bg-accent"
        aria-expanded={open}
      >
        {open ? "Hide skills" : "Explore skills"}
      </button>
      {open && (
        <div className="pt-2">
          <RevealQuest />
        </div>
      )}
    </div>
  );
}
